import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, MessageCircle, Phone, FileDown, ArrowRight, Store, Users, ShieldCheck, Sprout, ChefHat } from "lucide-react";
import Configurator from "@/components/franchise/Configurator";
import EnquiryForm from "@/components/franchise/EnquiryForm";
import CommunityForm from "@/components/franchise/CommunityForm";
import { PUB, EMPTY_CONFIG, inr } from "@/components/franchise/fq";
import "@/components/franchise/franchise.css";

const DRAFT_KEY = "pb_franchise_cfg_v1";
const TABS = [["details", "Franchise Details"], ["quote", "Choose Model & Get Quote"], ["invest", "Investment Options"]];

const loadDraft = () => {
  try { return { ...EMPTY_CONFIG, ...JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}") }; } catch { return EMPTY_CONFIG; }
};

const Header = ({ contact }) => (
  <header className="fq-header">
    <div className="fq-wrap flex items-center justify-between gap-4 py-4">
      <a href="https://purnabramha.com" className="flex items-center gap-3" data-testid="fq-brand-link">
        <img src="/pb-logo.png" alt="Purnabramha" className="h-11 w-auto" />
      </a>
      <nav className="flex items-center gap-2 text-sm">
        <a href="https://purnabramha.com" className="fq-navlink hidden sm:inline" data-testid="fq-nav-home">Home</a>
        <a href={`https://wa.me/${(contact?.whatsapp || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="fq-navlink" data-testid="fq-nav-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        <a href={`tel:${(contact?.phone || "").replace(/\s/g, "")}`} className="fq-navlink" data-testid="fq-nav-call"><Phone className="w-4 h-4" /> Call</a>
      </nav>
    </div>
  </header>
);

const Footer = ({ contact }) => (
  <footer className="fq-footer">
    <div className="fq-wrap py-10 grid md:grid-cols-3 gap-6 text-sm">
      <div><img src="/pb-logo.png" alt="Purnabramha" className="h-10 mb-3" /><p className="opacity-80">Authentic Maharashtrian vegetarian cuisine.</p></div>
      <div><p className="font-semibold mb-1">Contact</p><p>WhatsApp / Phone: {contact?.whatsapp}</p><p className="opacity-80">{contact?.hours}</p></div>
      <div><p className="font-semibold mb-1">Information</p><Link to="/franchise/privacy" className="underline" data-testid="fq-footer-privacy">Privacy notice</Link><p className="opacity-70 mt-2 text-xs">Indicative information only. No franchise is awarded, no return is promised and no territory is reserved through this page.</p></div>
    </div>
  </footer>
);

const PROCESS = ["Choose format", "Choose operating model", "Review every service", "Staffing if needed", "Review indicative quote", "Submit enquiry", "Discussion & site assessment", "Individual written proposal"];

function DetailsTab({ cat, go }) {
  return (
    <div className="space-y-14" data-testid="tab-details">
      <section className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-start">
        <div>
          <p className="fq-kicker">Why Purnabramha</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#3A0A06] leading-tight">A Maharashtrian vegetarian kitchen, built on tradition and women-led leadership.</h2>
          <p className="mt-4 text-base opacity-85 max-w-xl">Purnabramha brings authentic Maharashtrian thali, Vada Pav, Misal Pav and traditional recipes to cities across India and Australia. Partners work with documented recipes, SOPs and an intranet that keeps every outlet consistent.</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[[ChefHat, "Documented recipes & SOPs"], [ShieldCheck, "Clear, itemised charges"], [Sprout, "Three operating models"]].map(([I, t]) => (
              <div key={t} className="fq-feature"><I className="w-5 h-5 text-[#7A0000]" /><span>{t}</span></div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <button className="fq-cta-btn" onClick={() => go("quote", 1)} data-testid="details-start-quote">Start My Franchise Quote <ArrowRight className="w-4 h-4" /></button>
            <a className="fq-ghost-btn" href={`${PUB}/guide.pdf`} target="_blank" rel="noreferrer" data-testid="details-guide-link"><FileDown className="w-4 h-4" /> Current franchise guide (PDF)</a>
          </div>
        </div>
        <ol className="fq-process" data-testid="details-process">
          {PROCESS.map((p, i) => <li key={p}><span>{String(i + 1).padStart(2, "0")}</span>{p}</li>)}
        </ol>
      </section>
      <section>
        <p className="fq-kicker">Centre formats</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
          {cat.formats.map((f, i) => (
            <div key={f.id} className="fq-format-card" style={{ animationDelay: `${i * 80}ms` }} data-testid={`details-format-${f.id}`}>
              <div className="font-display text-2xl text-[#3A0A06]">{f.label}</div>
              <div className="mt-2 text-sm">{f.area}</div>
              <div className="text-sm opacity-70">{f.seats}</div>
              <div className="mt-5 text-xs uppercase tracking-widest opacity-60">Reference franchise fee</div>
              <div className="font-semibold text-lg">{inr(f.reference_fee)}</div>
            </div>
          ))}
        </div>
        <p className="text-xs opacity-70 mt-3">Reference fees are from the FOCO schedule. FOFO and Guided Operations fees per format await model-specific confirmation. Site and format suitability need assessment.</p>
      </section>
      <section>
        <p className="fq-kicker">Operating models</p>
        <div className="grid lg:grid-cols-3 gap-4 mt-3">
          {cat.models.map((m) => (
            <div key={m.id} className="fq-model-card" data-testid={`details-model-${m.id}`}>
              <div className="text-xs uppercase tracking-widest text-[#8A4B0F]">{m.full_name}</div>
              <div className="font-display text-2xl mt-1">{m.label}</div>
              <p className="text-sm mt-2 opacity-85">{m.summary}</p>
              <p className="text-sm mt-3 font-semibold text-[#7A0000]">
                {m.fixed_fee ? `${inr(m.fixed_fee.amount)} + GST / month` : `${m.royalty_bp / 100}% of ${m.royalty_basis}`}
                {m.profit_share ? ` · ${m.profit_share.partner_pct}:${m.profit_share.pb_pct} distributable profit` : ""} + services
              </p>
            </div>
          ))}
        </div>
      </section>
      <SetupReference cat={cat} />
    </div>
  );
}

function SetupReference({ cat }) {
  const cap = cat.capex;
  return (
    <section data-testid="details-capex-reference">
      <p className="fq-kicker">Setup, CAPEX & deposit reference</p>
      <p className="text-sm opacity-80 max-w-3xl">Shown separately from franchise and service fees. These are existing FOCO setup references, not fresh vendor quotes; deposits are not described as refundable unless the applicable terms say so.</p>
      <details className="fq-details mt-3">
        <summary>View the original reference schedule (all formats)</summary>
        <div className="overflow-x-auto mt-3">
          <table className="fq-table">
            <thead><tr><th>Item</th>{cat.formats.map((f) => <th key={f.id}>{f.label}</th>)}</tr></thead>
            <tbody>
              {cap.items.map((it) => (
                <tr key={it.id}><td>{it.label}</td>{cat.formats.map((f) => { const v = it.values[f.id]; return <td key={f.id}>{v.amount != null ? inr(v.amount) : v.status === "ACTUALS" ? "Actuals" : "Not priced"}</td>; })}</tr>
              ))}
              <tr className="font-semibold"><td>Priced CAPEX subtotal</td>{cat.formats.map((f) => <td key={f.id}>{inr(cap.stated_priced_capex_subtotals[f.id])}</td>)}</tr>
              <tr className="font-semibold"><td>Original reference schedule total*</td>{cat.formats.map((f) => <td key={f.id}>{inr(cap.original_totals[f.id])}</td>)}</tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs opacity-70 mt-2">*Includes the chart franchise fee and priced deposits; excludes actuals/unpriced items and newer service charges. Not “your final investment”.</p>
      </details>
    </section>
  );
}

function InvestTab({ go }) {
  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="tab-invest">
      <div className="fq-model-card">
        <Store className="w-6 h-6 text-[#7A0000]" />
        <h3 className="font-display text-2xl mt-3">Own an individual outlet</h3>
        <p className="text-sm mt-2 opacity-85">Outlet-ownership enquiries go through the guided configurator so you see an itemised, indicative quote before you enquire.</p>
        <button className="fq-cta-btn mt-6" onClick={() => go("quote", 1)} data-testid="invest-start-quote">Start My Franchise Quote <ArrowRight className="w-4 h-4" /></button>
      </div>
      <div className="fq-model-card">
        <Users className="w-6 h-6 text-[#7A0000]" />
        <h3 className="font-display text-2xl mt-3">Community-expansion updates</h3>
        <p className="text-sm mt-2 mb-5 opacity-85">Only want news about Purnabramha's growth? Join the updates list — no restaurant quote needed. This is enquiry/update-only: no payments, securities, share reservations, fixed returns or contribution campaigns.</p>
        <CommunityForm />
      </div>
    </div>
  );
}

export default function FranchisePublic() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([t]) => t === params.get("tab")) ? params.get("tab") : "details";
  const step = Math.min(6, Math.max(1, Number(params.get("step")) || 1));
  const [cat, setCat] = useState(null);
  const [err, setErr] = useState(null);
  const [config, setConfigState] = useState(loadDraft);

  useEffect(() => {
    document.title = "Franchise | Purnabramha";
    fetch(`${PUB}/catalogue`).then((r) => (r.ok ? r.json() : Promise.reject(new Error("Unable to load")))).then((d) => setCat(d.catalogue)).catch((e) => setErr(e.message));
  }, []);

  const setConfig = (c) => { setConfigState(c); sessionStorage.setItem(DRAFT_KEY, JSON.stringify(c)); };
  const embed = params.get("embed") === "1";
  const go = useCallback((t, s) => setParams(t === "quote" ? { tab: t, step: String(s || step), ...(embed ? { embed: "1" } : {}) } : { tab: t, ...(embed ? { embed: "1" } : {}) }), [setParams, step, embed]);
  const setStep = useCallback((s) => setParams({ tab: "quote", step: String(s), ...(embed ? { embed: "1" } : {}) }), [setParams, embed]);

  return (
    <div className="fq-root">
      {!embed && <Header contact={cat?.settings?.contact} />}
      {!embed && <section className="fq-hero">
        <div className="fq-wrap py-14 md:py-20">
          <p className="fq-kicker text-[#F3D9A4]">Franchise Opportunities</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FBF6EE] max-w-3xl leading-[1.05]">Bring authentic Maharashtrian flavours to your city.</h1>
          <p className="text-base md:text-lg text-[#F3E6D3] mt-5 max-w-2xl">Choose a centre format and operating model, review every service, and see an itemised indicative quote — before you share any contact details.</p>
        </div>
      </section>}
      <div className={`fq-tabs-bar ${embed ? "is-embed" : ""}`} role="tablist" aria-label="Franchise sections">
        <div className="fq-wrap flex overflow-x-auto">
          {TABS.map(([t, l]) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => go(t, t === "quote" ? step : undefined)} data-testid={`tab-${t}`} className={`fq-tab ${tab === t ? "is-active" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      <main className="fq-wrap py-12 min-h-[50vh]">
        {err && <div className="fq-callout danger" data-testid="fq-load-error">Could not load franchise information. Please refresh.</div>}
        {!cat && !err && <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {cat && tab === "details" && <DetailsTab cat={cat} go={go} />}
        {cat && tab === "quote" && (
          <Configurator cat={cat} config={config} setConfig={setConfig} step={step} setStep={setStep}
            renderFinal={({ result }) => <EnquiryForm config={config} result={result} />} />
        )}
        {cat && tab === "invest" && <InvestTab go={go} />}
      </main>
      {!embed && <Footer contact={cat?.settings?.contact} />}
    </div>
  );
}
